/**
 * @module FactPackPanel.parts
 * @purpose Sub-component groups for FactPackPanel — Counts, References, Temporal, Hashes.
 *   Barrel re-export; decomposed to keep FactPackPanel.tsx well under 500 lines.
 * @depends types/reasoning, react-i18next, HashConflictResolver
 * @dependedOnBy FactPackPanel
 *
 * @rules
 * - Performance: pure display — all data passed via props; no data fetching
 * - State: none — stateless presentational components
 * - Errors: n/a — purely presentational
 */

import { useTranslation } from 'react-i18next'
import type { ClusterDetail, ClusterBriefing, HashResolution } from '../../../../../types/reasoning'
import { HashConflictResolver } from './HashConflictResolver'

const SECTION_HEADING: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '0.74rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: '6px',
  marginTop: '12px',
}

const MONO_LABEL: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.68rem',
  color: 'var(--text-muted)',
}

const MONO_VALUE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.68rem',
  color: 'var(--text-dim)',
}

const WARNING_INLINE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.64rem',
  color: 'rgba(220, 150, 50, 0.85)',
  marginLeft: '6px',
}

const OBS_ITEM: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.76rem',
  color: 'var(--text-dim)',
  marginBottom: '3px',
  paddingLeft: '4px',
}

interface ObservationListProps {
  observations: string[]
}

function ObservationList({ observations }: ObservationListProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  if (observations.length === 0) return <></>
  return (
    <div style={{ marginTop: '6px' }}>
      <div style={{ ...MONO_LABEL, marginBottom: '3px' }}>
        {t('reasoning.briefingBoard.factPack.observations.heading' as never)}
      </div>
      <ul style={{ listStyle: 'disc', paddingLeft: '16px', margin: 0 }}>
        {observations.map((obs) => (
          <li key={obs} style={OBS_ITEM}>{obs}</li>
        ))}
      </ul>
    </div>
  )
}

interface CountsGroupProps {
  detail: ClusterDetail
  briefing: ClusterBriefing | null
}

/**
 * Evidence counts group — semantic summary counts + execution context observations.
 *
 * @behaviour Renders presence/reference/execution_evidence counts from semanticSummary,
 *   a hash_summary line derived client-side from fileHashes (NFR1 permitted derivation),
 *   and execution_context_observations from the briefing adjacent to the counts.
 * @errors n/a — purely presentational
 */
export function CountsGroup({ detail, briefing }: CountsGroupProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const { semanticSummary, fileHashes } = detail

  const knownMd5 = Object.values(fileHashes).filter((e) => e.md5 !== null).length
  const knownSha256 = Object.values(fileHashes).filter((e) => e.sha256 !== null).length
  const totalFiles = Object.keys(fileHashes).length

  return (
    <div>
      <div style={SECTION_HEADING}>
        {t('reasoning.briefingBoard.factPack.counts.heading' as never)}
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <tbody>
          <tr>
            <td style={{ ...MONO_LABEL, paddingRight: '12px', paddingBottom: '2px' }}>
              {t('reasoning.briefingBoard.factPack.counts.presence' as never)}
            </td>
            <td style={{ ...MONO_VALUE, paddingBottom: '2px' }}>{semanticSummary.presence}</td>
          </tr>
          <tr>
            <td style={{ ...MONO_LABEL, paddingRight: '12px', paddingBottom: '2px' }}>
              {t('reasoning.briefingBoard.factPack.counts.reference' as never)}
            </td>
            <td style={{ ...MONO_VALUE, paddingBottom: '2px' }}>{semanticSummary.reference}</td>
          </tr>
          <tr>
            <td style={{ ...MONO_LABEL, paddingRight: '12px' }}>
              {t('reasoning.briefingBoard.factPack.counts.executionEvidence' as never)}
            </td>
            <td style={MONO_VALUE}>{semanticSummary.execution_evidence}</td>
          </tr>
        </tbody>
      </table>
      {totalFiles > 0 && (
        <div style={{ ...MONO_LABEL, marginTop: '5px' }}>
          {t('reasoning.briefingBoard.factPack.counts.hashSummary' as never, {
            knownMd5,
            knownSha256,
            total: totalFiles,
          })}
        </div>
      )}
      <ObservationList observations={briefing?.execution_context_observations ?? []} />
    </div>
  )
}

interface ReferencesGroupProps {
  referenceSummary: Record<string, number>
}

/**
 * Process references group — full process→count map.
 *
 * @behaviour Renders every process name and its count from referenceSummary.
 *   Empty map → "no process-linked references" note.
 * @errors n/a — purely presentational
 */
export function ReferencesGroup({ referenceSummary }: ReferencesGroupProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const entries = Object.entries(referenceSummary)

  return (
    <div>
      <div style={SECTION_HEADING}>
        {t('reasoning.briefingBoard.factPack.references.heading' as never)}
      </div>
      {entries.length === 0 ? (
        <span style={MONO_LABEL}>
          {t('reasoning.briefingBoard.factPack.references.empty' as never)}
        </span>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {entries.map(([process, count]) => (
              <tr key={process}>
                <td style={{ ...MONO_VALUE, paddingRight: '12px', paddingBottom: '2px' }}>
                  {process}
                </td>
                <td style={{ ...MONO_LABEL, paddingBottom: '2px' }}>{count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

interface TemporalGroupProps {
  detail: ClusterDetail
  briefing: ClusterBriefing | null
}

/**
 * Temporal enrichment group — per-file temporal records + temporal observations.
 *
 * @behaviour Renders SI/FN timestamps per file; flags SI < FN (possible timestomping).
 *   Adjacent temporal_observations from briefing.
 * @errors n/a — purely presentational
 */
export function TemporalGroup({ detail, briefing }: TemporalGroupProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const entries = Object.entries(detail.temporalEnrichment)

  return (
    <div>
      <div style={SECTION_HEADING}>
        {t('reasoning.briefingBoard.factPack.temporal.heading' as never)}
      </div>
      {entries.length === 0 ? (
        <span style={MONO_LABEL}>
          {t('reasoning.briefingBoard.factPack.temporal.empty' as never)}
        </span>
      ) : (
        <div>
          {entries.map(([filePath, rec]) => (
            <div key={filePath} style={{ marginBottom: '8px' }}>
              <div style={{ ...MONO_VALUE, marginBottom: '2px' }}>{filePath}</div>
              {rec.siLtFn && (
                <span style={WARNING_INLINE}>
                  {t('reasoning.briefingBoard.factPack.temporal.siLtFn' as never)}
                </span>
              )}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '2px' }}>
                {(['created', 'modified', 'accessed'] as const).map((field) => (
                  <span key={field} style={MONO_LABEL}>
                    {t(`reasoning.briefingBoard.factPack.temporal.${field}` as never)}
                    {': '}
                    {rec.si[field] ?? '—'}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <ObservationList observations={briefing?.temporal_observations ?? []} />
    </div>
  )
}

interface HashRowProps {
  basename: string
  entry: import('../../../../../types/reasoning').FileHashEntry
  onResolveHash?: (resolution: HashResolution) => Promise<void>
}

/**
 * Single file hash row — shows basename, MD5, SHA-256.
 * Absent value → placeholder + "not provided by source" warning (FR6).
 *
 * @behaviour Present values shown directly. Null → placeholder + warning. Never blank, never
 *   fabricated.
 * @errors n/a — purely presentational
 */
export function HashRow({ basename, entry, onResolveHash }: HashRowProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ ...MONO_VALUE, marginBottom: '3px' }}>{basename}</div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <span>
          <span style={MONO_LABEL}>{t('reasoning.briefingBoard.factPack.hashes.md5Label' as never)}{': '}</span>
          {entry.md5 !== null ? (
            <span style={MONO_VALUE}>{entry.md5}</span>
          ) : (
            <>
              <span style={MONO_LABEL}>{'—'}</span>
              <span style={WARNING_INLINE}>
                {t('reasoning.briefingBoard.factPack.hashes.notProvided' as never)}
              </span>
            </>
          )}
        </span>
        <span>
          <span style={MONO_LABEL}>{t('reasoning.briefingBoard.factPack.hashes.sha256Label' as never)}{': '}</span>
          {entry.sha256 !== null ? (
            <span style={MONO_VALUE}>{entry.sha256}</span>
          ) : (
            <>
              <span style={MONO_LABEL}>{'—'}</span>
              <span style={WARNING_INLINE}>
                {t('reasoning.briefingBoard.factPack.hashes.notProvided' as never)}
              </span>
            </>
          )}
        </span>
      </div>
      <HashConflictResolver
        basename={basename}
        entry={entry}
        {...(onResolveHash !== undefined ? { onResolveHash } : {})}
      />
    </div>
  )
}

interface HashesGroupProps {
  detail: ClusterDetail
  briefing: ClusterBriefing | null
  onResolveHash?: (resolution: HashResolution) => Promise<void>
}

/**
 * Hashes + LOLBAS group — per-file hash rows with conflict resolution,
 *   LOLBAS/shimcache enrichment, and hash_and_lolbas_observations.
 *
 * @behaviour Renders each basename from fileHashes with HashRow.
 *   Empty fileHashes → "no file hashes" note.
 * @errors n/a — purely presentational
 */
export function HashesGroup({ detail, briefing, onResolveHash }: HashesGroupProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const hashEntries = Object.entries(detail.fileHashes)
  const lolbasEntries = Object.entries(detail.lolbasEnrichment)
  const shimEntries = Object.entries(detail.shimcacheEnrichment)

  return (
    <div>
      <div style={SECTION_HEADING}>
        {t('reasoning.briefingBoard.factPack.hashes.heading' as never)}
      </div>
      {hashEntries.length === 0 ? (
        <span style={MONO_LABEL}>
          {t('reasoning.briefingBoard.factPack.hashes.empty' as never)}
        </span>
      ) : (
        <div>
          {hashEntries.map(([basename, entry]) => (
            <HashRow
              key={basename}
              basename={basename}
              entry={entry}
              {...(onResolveHash !== undefined ? { onResolveHash } : {})}
            />
          ))}
        </div>
      )}
      {lolbasEntries.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <div style={MONO_LABEL}>{t('reasoning.briefingBoard.factPack.hashes.lolbasHeading' as never)}</div>
          {lolbasEntries.map(([key]) => (
            <div key={key} style={{ ...MONO_VALUE, marginTop: '2px' }}>{key}</div>
          ))}
        </div>
      )}
      {shimEntries.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <div style={MONO_LABEL}>{t('reasoning.briefingBoard.factPack.hashes.shimcacheHeading' as never)}</div>
          {shimEntries.map(([key]) => (
            <div key={key} style={{ ...MONO_VALUE, marginTop: '2px' }}>{key}</div>
          ))}
        </div>
      )}
      <ObservationList observations={briefing?.hash_and_lolbas_observations ?? []} />
    </div>
  )
}
